import { useNavigate } from "react-router-dom";
import { FaWpforms, FaUsers, FaChartBar, FaLayerGroup, FaFacebook } from "react-icons/fa";
import { SiMeta } from "react-icons/si";
import { PublicNav, PublicFooter } from "../components/PublicLayout";
import "./LandingPage.css";

const LandingPage = () => {
  const navigate = useNavigate();

  return (
    <div className="lp-root">

      <PublicNav />

      {/* ── Hero ── */}
      <section className="lp-hero">
        <div className="lp-hero-inner">
          <div className="lp-hero-badge">
            <SiMeta style={{ fontSize: "0.9rem" }} /> Meta Ads Platform
          </div>
          <h1 className="lp-hero-title">
            Run Facebook Ads<br />
            <span className="lp-hero-gradient">Without the Overwhelm</span>
          </h1>
          <p className="lp-hero-sub">
            Meta Ads Manager is powerful but complex. Adsoar gives you a guided
            workflow to create, manage, and track campaigns — without getting lost in the settings.
          </p>
          <div className="lp-hero-ctas">
            <button className="lp-hero-btn-main" onClick={() => navigate("/signup")}>Get Started Free</button>
            <button className="lp-hero-btn-ghost" onClick={() => navigate("/login")}>Log in →</button>
          </div>
          <p className="lp-hero-trust">Connect your Meta account in minutes • No credit card required</p>
        </div>
      </section>

      {/* ── Features ── */}
      <section className="lp-features">
        <div className="lp-section-inner">
          <div className="lp-section-head">
            <h2 className="lp-section-title">Everything you need to run ads</h2>
            <p className="lp-section-sub">Built around how marketers actually work</p>
          </div>
          <div className="lp-features-grid">
            {[
              {
                icon: <FaWpforms />,
                title: "Guided Campaign Builder",
                desc: "Step-by-step wizard walks you through every decision — objective, audience, creative, budget. No guessing.",
              },
              {
                icon: <FaUsers />,
                title: "Audience Management",
                desc: "Build and save custom audiences. Reuse them across campaigns without rebuilding from scratch every time.",
              },
              {
                icon: <FaChartBar />,
                title: "Performance Overview",
                desc: "Monitor impressions, clicks, spend, and CTR from a clean dashboard. See what's working at a glance.",
              },
              {
                icon: <FaLayerGroup />,
                title: "Multi-Account Support",
                desc: "Manage multiple ad accounts and switch between them instantly. Great for agencies and freelancers.",
              },
              {
                icon: <FaFacebook />,
                title: "Facebook & Instagram",
                desc: "Create ads that run on Facebook and Instagram. Manage both platforms from one place.",
              },
              {
                icon: <SiMeta />,
                title: "Direct Meta Integration",
                desc: "Connect your Meta Business account securely via OAuth. Your data goes straight to the source.",
              },
            ].map((f, i) => (
              <div className="lp-feature-card" key={i}>
                <div className="lp-feature-icon">{f.icon}</div>
                <h3 className="lp-feature-title">{f.title}</h3>
                <p className="lp-feature-desc">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How it works ── */}
      <section className="lp-how">
        <div className="lp-section-inner">
          <div className="lp-section-head">
            <h2 className="lp-section-title">Up and running in minutes</h2>
            <p className="lp-section-sub">Three steps, no technical setup required</p>
          </div>
          <div className="lp-steps">
            {[
              { n: "1", title: "Connect", desc: "Link your Facebook Business account via Meta's secure login. Takes about 60 seconds." },
              { n: "2", title: "Create", desc: "Launch your first campaign using the step-by-step wizard. Smart defaults, clear choices." },
              { n: "3", title: "Track", desc: "Monitor performance from your dashboard. See spend, reach, clicks, and results in one view." },
            ].map((s, i) => (
              <div className="lp-step" key={i}>
                <div className="lp-step-num">{s.n}</div>
                <h3 className="lp-step-title">{s.title}</h3>
                <p className="lp-step-desc">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Pricing ── */}
      <section className="lp-pricing">
        <div className="lp-section-inner">
          <div className="lp-section-head">
            <h2 className="lp-section-title">Simple, transparent pricing</h2>
            <p className="lp-section-sub">No hidden fees. Cancel anytime.</p>
          </div>
          <div className="lp-plans">

            <div className="lp-plan">
              <div className="lp-plan-name">Starter</div>
              <div className="lp-plan-price"><span className="lp-plan-amount">$99</span><span className="lp-plan-per">/mo</span></div>
              <ul className="lp-plan-features">
                <li>Up to $10,000 monthly ad spend</li>
                <li>Campaign creation wizard</li>
                <li>Audience management</li>
                <li>Performance dashboard</li>
                <li>Email support</li>
              </ul>
              <button className="lp-plan-btn" onClick={() => navigate("/signup")}>Get Started</button>
            </div>

            <div className="lp-plan lp-plan-featured">
              <div className="lp-plan-badge">Most Popular</div>
              <div className="lp-plan-name">Professional</div>
              <div className="lp-plan-price"><span className="lp-plan-amount">$299</span><span className="lp-plan-per">/mo</span></div>
              <ul className="lp-plan-features">
                <li>Up to $50,000 monthly ad spend</li>
                <li>Everything in Starter</li>
                <li>Multi-account management</li>
                <li>Advanced reporting</li>
                <li>Priority support</li>
              </ul>
              <button className="lp-plan-btn lp-plan-btn-featured" onClick={() => navigate("/signup")}>Get Started</button>
            </div>

            <div className="lp-plan">
              <div className="lp-plan-name">Enterprise</div>
              <div className="lp-plan-price"><span className="lp-plan-amount">$999</span><span className="lp-plan-per">/mo</span></div>
              <ul className="lp-plan-features">
                <li>Unlimited ad spend</li>
                <li>Everything in Professional</li>
                <li>Dedicated account manager</li>
                <li>Custom onboarding</li>
                <li>Priority phone support</li>
              </ul>
              <button className="lp-plan-btn lp-plan-btn-outline" onClick={() => navigate("/signup")}>Contact Us</button>
            </div>

          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="lp-cta">
        <div className="lp-cta-inner">
          <h2 className="lp-cta-title">Ready to simplify your ad workflow?</h2>
          <p className="lp-cta-sub">Connect your Meta account and launch your first campaign today.</p>
          <button className="lp-hero-btn-main" onClick={() => navigate("/signup")}>Get Started Free</button>
          <p className="lp-hero-trust" style={{ color: "rgba(255,255,255,0.6)" }}>No credit card required</p>
        </div>
      </section>

      <PublicFooter />

    </div>
  );
};

export default LandingPage;
